import React, { useState, useEffect } from 'react';
import { SkipForward } from 'lucide-react';
import '../App.css';
import './SortingHat.css';
import CollegeWizard from '../components/CollegeWizard';
import sortingHatImg from '../media/sortinghat.png';

function hasSavedProgress() {
  try {
    const raw = localStorage.getItem('school-picker-state');
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    // Only skip the intro when there's genuine progress — at least one school
    // added or past step 1. An empty step-1 state (e.g. after a restart)
    // should replay the intro animation.
    return (
      typeof parsed?.step === 'number' &&
      Array.isArray(parsed?.schools) &&
      (parsed.step > 1 || parsed.schools.length > 0)
    );
  } catch {
    return false;
  }
}

const SPEECH = [
  "Hmm... yes... a mind full of questions, and a future not yet chosen.",
  "Many worthy halls I see before you, each with its own peculiar magic.",
  "Very well, then. Place your choices before me, and I'll sort the matter out.",
];
const LINE_DURATION = 3000;

const HAT_QUIPS = [
  "Still deliberating? A fine quality, that.",
  "You know, in a thousand years of sorting, I've never seen a case quite like yours.",
  "Between you and me? Trust your gut. I usually do.",
  "The mind that questions is the mind that grows. Keep at it.",
  "Some choices sort themselves. This one clearly deserves more brim-pondering.",
  "Patience. Even I took a moment before sorting Harry Potter.",
  "Ah, you again. I was just thinking about you, actually.",
  "Not all who wander are lost. Some are just choosing a college.",
  "I've sorted thousands. The ones who overthink it usually land on their feet.",
  "Curious... I sense great potential. And perhaps a touch of indecision.",
  "You know what the Founders never told me? Which house had the best career outcomes.",
  "Don't tell Dumbledore, but this decision matters more than your house placement ever did.",
];

export default function SortingHat() {
  const [phase, setPhase] = useState(() => hasSavedProgress() ? 'revealed' : 'intro');
  const [speechStep, setSpeechStep] = useState(0);
  const [verdictMode, setVerdictMode] = useState(false);
  const [hatJumping, setHatJumping] = useState(false);
  const [currentQuip, setCurrentQuip] = useState(null);

  const revealContent = () => {
    setSpeechStep(SPEECH.length);
    setPhase('revealed');
  };

  const handleCornerHatClick = () => {
    const pool = HAT_QUIPS.filter(q => q !== currentQuip);
    setCurrentQuip(pool[Math.floor(Math.random() * pool.length)]);
    setHatJumping(true);
  };

  useEffect(() => {
    const prev = document.title;
    document.title = 'The Sorting Hat';
    // Prevent the browser from restoring the previous scroll position on reload
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
    window.scrollTo(0, 0);
    document.documentElement.style.overscrollBehavior = 'none';
    document.body.style.overscrollBehavior = 'none';
    return () => {
      document.title = prev;
      document.documentElement.style.overscrollBehavior = '';
      document.body.style.overscrollBehavior = '';
    };
  }, []);

  useEffect(() => {
    if (phase !== 'speaking' || speechStep >= SPEECH.length) return;
    const t = setTimeout(() => setSpeechStep(s => s + 1), LINE_DURATION);
    return () => clearTimeout(t);
  }, [phase, speechStep]);

  // Auto-dismiss quip after 5 seconds
  useEffect(() => {
    if (!currentQuip) return;
    const t = setTimeout(() => setCurrentQuip(null), 5000);
    return () => clearTimeout(t);
  }, [currentQuip]);

  return (
    <div className={`sh-theme sh-page${phase !== 'revealed' ? ' sh-page--intro' : ''}`}>
      {phase === 'revealed' ? (
        <>
          {!verdictMode && (
            <>
              <button
                className="sh-hat-corner-wrap sh-hat-corner-btn"
                onClick={handleCornerHatClick}
                aria-label="Poke the Sorting Hat"
              >
                <img
                  src={sortingHatImg}
                  alt="Sorting Hat"
                  draggable="false"
                  className={`sh-hat-img${hatJumping ? ' sh-hat-jump' : ''}`}
                  onAnimationEnd={() => setHatJumping(false)}
                />
              </button>
              {currentQuip && (
                <div
                  key={currentQuip}
                  className="sh-hat-quip-bubble"
                  onClick={() => setCurrentQuip(null)}
                >
                  <em>{currentQuip}</em>
                </div>
              )}
            </>
          )}
          <div className="sh-content-wrap">
            <CollegeWizard hideHeader wrapperClass="" hpTheme onHatVerdictModeChange={setVerdictMode} />
          </div>
        </>
      ) : (
        <div className="sh-hat-area">
          <button className="sh-skip-btn" onClick={revealContent} aria-label="Fast forward">
            <SkipForward size={18} />
          </button>
          <div className={`sh-hat-float-wrap${phase !== 'intro' ? ' sh-floating' : ''}`}>
            <img
              src={sortingHatImg}
              alt="Sorting Hat"
              draggable="false"
              className={`sh-hat-img${phase === 'intro' ? ' sh-wobble' : ''}`}
              onAnimationEnd={() => phase === 'intro' && setPhase('speaking')}
            />
          </div>
          {phase === 'speaking' && (
            <div className="sh-speech">
              {speechStep < SPEECH.length ? (
                <p key={speechStep} className="sh-speech-line">
                  <em>{SPEECH[speechStep]}</em>
                </p>
              ) : (
                <button key="btn" className="sh-reveal-btn" onClick={revealContent}>
                  Let The Hat Begin →
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
